import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { HiPencil, HiTrash } from 'react-icons/hi'
import {
  goToEditPensionCalculation,
  onDeletePensionCalculationAsync,
  onGetPensionCalculationsAsync,
  onUpdatePensionCalculationStatusAsync
} from './PensionCalculation.slice'
import Spinner from '../../components/Spinners/Spinner'
import ConfirmModal from '../../components/Modal/ConfirmModal'
import routes from '../../config/settings/routes'
import { useAcl } from '../AccessControlList/acl.hook'
import {
  formatPaidOnLabel,
  getMonthKey,
  PENSION_PERIOD_STATUS,
  resolvePensionPeriodStatus,
} from '../../config/resources/utils'
import * as S from './PensionCalculation.styles'

const isPaid = (status) => status === 'payed'

const formatPeriod = (item) => item?.targetMonth || ''

const STATUS_CHIP = {
  [PENSION_PERIOD_STATUS.UPCOMING]: { label: 'Próximo', tone: 'upcoming' },
  [PENSION_PERIOD_STATUS.DUE]: { label: 'Por pagar', tone: 'due' },
  [PENSION_PERIOD_STATUS.OVERDUE]: { label: 'Atrasado', tone: 'overdue' },
}

const getYear = (item) => {
  if (item?.month) {
    const year = new Date(item.month).getFullYear()
    if (!Number.isNaN(year)) return year
  }
  return 'Sin año'
}

const groupByYear = (items) =>
  items.reduce((acc, item) => {
    const year = getYear(item)
    if (!acc[year]) acc[year] = []
    acc[year].push(item)
    return acc
  }, {})

const sortYearsDesc = (years) =>
  [...years].sort((a, b) => {
    if (a === 'Sin año') return 1
    if (b === 'Sin año') return -1
    return Number(b) - Number(a)
  })

/**
 * Build per-user year timelines. Keeps each user independent when admins
 * see multiple accounts in the same list.
 */
const buildUserGroups = (items) => {
  const byUser = items.reduce((acc, item) => {
    const key = item.userId || item.userName || 'unknown'
    if (!acc[key]) {
      acc[key] = {
        key,
        userName: item.userName || 'Sin nombre',
        items: [],
      }
    }
    acc[key].items.push(item)
    return acc
  }, {})

  return Object.values(byUser).map((group) => {
    const groupedByYear = groupByYear(group.items)
    return {
      ...group,
      groupedByYear,
      years: sortYearsDesc(Object.keys(groupedByYear)),
    }
  })
}

const ActionButtons = ({ onEdit, onDelete }) => (
  <S.CardActions>
    <S.IconButton
      type="button"
      onClick={onEdit}
      aria-label="Modificar registro"
      title="Modificar"
    >
      <HiPencil aria-hidden />
    </S.IconButton>
    <S.IconButton
      type="button"
      $danger
      onClick={onDelete}
      aria-label="Eliminar registro"
      title="Eliminar"
    >
      <HiTrash aria-hidden />
    </S.IconButton>
  </S.CardActions>
)

const CalculationCard = ({
  item,
  isAdmin,
  periodStatus,
  updating,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const paid = periodStatus === PENSION_PERIOD_STATUS.PAID
  const upcoming = periodStatus === PENSION_PERIOD_STATUS.UPCOMING
  const needsAttention =
    periodStatus === PENSION_PERIOD_STATUS.DUE ||
    periodStatus === PENSION_PERIOD_STATUS.OVERDUE
  const chip = STATUS_CHIP[periodStatus]

  return (
    <S.PensionCard $emphasis={needsAttention} $overdue={periodStatus === PENSION_PERIOD_STATUS.OVERDUE}>
      <S.CardBody>
        <S.CardTop>
          <S.CardIdentity>
            <S.CardPeriod>
              {formatPeriod(item)}
              {chip && (
                <S.StatusChip $tone={chip.tone}>{chip.label}</S.StatusChip>
              )}
            </S.CardPeriod>
            {isAdmin && (
              <S.CardUser>{item.userName || 'Sin nombre'}</S.CardUser>
            )}
          </S.CardIdentity>
          <S.CardValueWrap>
            <S.CardValue>{item.calculatedAmount}</S.CardValue>
            {isAdmin && (
              <ActionButtons
                onEdit={() => onEdit(item.id)}
                onDelete={() => onDelete(item.id)}
              />
            )}
          </S.CardValueWrap>
        </S.CardTop>
      </S.CardBody>

      <S.CardFooter $paid={paid} $upcoming={upcoming}>
        {paid ? (
          <>
            <S.PaymentCopy $paid>
              {formatPaidOnLabel(item.paymentAt)}
            </S.PaymentCopy>
            <S.UndoLink
              type="button"
              disabled={updating}
              onClick={() => onToggleStatus(item)}
              aria-label={`Marcar pensión de ${formatPeriod(item)} como pendiente`}
            >
              {updating ? '...' : 'Deshacer'}
            </S.UndoLink>
          </>
        ) : upcoming ? (
          <S.PaymentCopy>Todavía no toca pagarlo</S.PaymentCopy>
        ) : (
          <>
            <S.PaymentCopy>
              {periodStatus === PENSION_PERIOD_STATUS.OVERDUE
                ? 'Pago atrasado'
                : 'Pendiente de pago'}
            </S.PaymentCopy>
            <S.PaymentLink
              type="button"
              disabled={updating}
              onClick={() => onToggleStatus(item)}
              aria-label={`Registrar pago de pensión de ${formatPeriod(item)}`}
            >
              {updating ? '...' : 'Registrar pago'}
            </S.PaymentLink>
          </>
        )}
      </S.CardFooter>
    </S.PensionCard>
  )
}

const UserCalculationGroup = ({
  group,
  showUserHeading,
  isAdmin,
  updatingId,
  onEdit,
  onDelete,
  onToggleStatus,
}) => (
  <S.UserGroup>
    {showUserHeading && (
      <S.UserGroupHeader>
        <S.UserGroupTitle>{group.userName}</S.UserGroupTitle>
        <S.SectionHint>
          {group.items.length} registro
          {group.items.length === 1 ? '' : 's'}
        </S.SectionHint>
      </S.UserGroupHeader>
    )}

    {group.years.map((year) => (
      <S.YearGroup key={year}>
        <S.YearLabel>{year}</S.YearLabel>
        <S.CardsWrapper>
          {group.groupedByYear[year].map((item) => {
            const periodStatus = resolvePensionPeriodStatus({
              month: item.month,
              monthKey: getMonthKey(item.month),
              status: item.status,
            })

            return (
              <CalculationCard
                key={item.id}
                item={item}
                isAdmin={isAdmin}
                periodStatus={periodStatus}
                updating={updatingId === item.id}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleStatus={onToggleStatus}
              />
            )
          })}
        </S.CardsWrapper>
      </S.YearGroup>
    ))}
  </S.UserGroup>
)

const PensionCalculationList = () => {
  const {
    error,
    loading,
    updatingId = null,
    pensionCalculations = [],
  } = useSelector((state) => state.adminPensionCalculations)
  const { isAdmin } = useAcl()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const userGroups = useMemo(
    () => buildUserGroups(pensionCalculations),
    [pensionCalculations]
  )
  const showUserHeading = isAdmin && userGroups.length > 1

  const handleEdit = (id) => {
    dispatch(goToEditPensionCalculation({ id }))
    navigate(routes.pensionCalculationsEdit)
  }

  const handleDeleteClick = (id) => {
    setSelectedId(id)
    setModalOpen(true)
  }

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(onDeletePensionCalculationAsync({ id: selectedId }))
      posthog.capture('pension_calculation_deleted')
    }
    setModalOpen(false)
    setSelectedId(null)
  }

  const handleCancelDelete = () => {
    setModalOpen(false)
    setSelectedId(null)
  }

  const handleToggleStatus = (row) => {
    const newStatus = isPaid(row.status) ? 'pending' : 'payed'
    const paymentAt = newStatus === 'payed' ? new Date() : null

    const paymentEventProps = {
      source: 'calculations_list',
      period: row.targetMonth,
      amount: row.amount != null ? Number(row.amount) : null,
    }
    if (newStatus === 'payed') {
      posthog.capture('pension_payment_registered', paymentEventProps)
    } else {
      posthog.capture('pension_payment_undone', paymentEventProps)
    }

    dispatch(
      onUpdatePensionCalculationStatusAsync({
        id: row.id,
        status: newStatus,
        paymentAt,
      })
    )
  }

  useEffect(() => {
    dispatch(onGetPensionCalculationsAsync())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <S.Container>
      {loading && <Spinner />}
      {!loading && error && (
        <S.ErrorText>No pudimos cargar los cálculos. Inténtalo de nuevo.</S.ErrorText>
      )}

      {!loading && !error && pensionCalculations.length === 0 && (
        <S.EmptyState>
          <strong>Sin cálculos todavía</strong>
          <span>
            Cuando existan, verás aquí el monto, el estado de pago y el
            historial por período.
          </span>
        </S.EmptyState>
      )}

      {!loading && !error && pensionCalculations.length > 0 && (
        userGroups.map((group) => (
          <UserCalculationGroup
            key={group.key}
            group={group}
            showUserHeading={showUserHeading}
            isAdmin={isAdmin}
            updatingId={updatingId}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onToggleStatus={handleToggleStatus}
          />
        ))
      )}

      <ConfirmModal
        isOpen={modalOpen}
        title="Eliminar cálculo"
        message="¿Seguro que quieres eliminar este cálculo de pensión? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </S.Container>
  )
}

export default PensionCalculationList
